import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, ShieldCheck, ShieldAlert, Building2, Pencil } from "lucide-react";

const APPROVAL_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  suspended: "bg-red-50 text-red-800 border-red-200",
  rejected: "bg-gray-100 text-gray-600 border-gray-300",
};

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86400000);
}

export default function Haulers() {
  const [haulers, setHaulers] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => setHaulers((await api.get("/haulers")).data), []);
  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (id) => {
    try {
      await api.post(`/haulers/${id}/approve`);
      toast.success("Hauler approved");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this hauler?")) return;
    await api.delete(`/haulers/${id}`);
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Haulers"
        subtitle="Approved carriers"
        actions={
          <button onClick={() => setEditing("new")} data-testid="new-hauler-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + Register Hauler
          </button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="haulers-list">
          {haulers.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">
              <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              No haulers registered yet.
            </div>
          ) : haulers.map((h) => {
            const insDays = daysUntil(h.insurance_expiry);
            const insWarn = insDays !== null && insDays <= 30;
            const insExpired = insDays !== null && insDays < 0;
            return (
              <div key={h.id} className="bg-white border border-gray-200 p-5" data-testid={`hauler-card-${h.company_name}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-gray-900 text-lg">{h.company_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{h.ein || "EIN —"}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${APPROVAL_STYLE[h.approval_status]}`}>
                    {h.approval_status}
                  </span>
                </div>

                <div className="border-t border-gray-100 pt-3 text-sm space-y-1 text-gray-700">
                  <div>{h.contact_name}</div>
                  <div className="font-mono text-xs">{h.contact_phone} · {h.contact_email}</div>
                  {h.address && <div className="text-xs">{h.address}</div>}
                </div>

                <div className="border-t border-gray-100 pt-3 mt-3">
                  <div className="label-caps mb-1">Insurance</div>
                  <div className="flex items-center gap-2 text-xs">
                    {insExpired ? (
                      <span className="flex items-center gap-1 text-red-700 font-bold"><ShieldAlert className="w-3.5 h-3.5" /> EXPIRED</span>
                    ) : insWarn ? (
                      <span className="flex items-center gap-1 text-amber-700 font-bold"><ShieldAlert className="w-3.5 h-3.5" /> {insDays}d remaining</span>
                    ) : h.insurance_expiry ? (
                      <span className="flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Valid</span>
                    ) : (
                      <span className="text-gray-500">Not provided</span>
                    )}
                    <span className="font-mono text-gray-500 ml-auto">{h.insurance_expiry || "—"}</span>
                  </div>
                  <div className="text-xs font-mono text-gray-500 mt-1">{h.insurance_carrier} {h.insurance_policy_number ? `· ${h.insurance_policy_number}` : ""}</div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                  {h.approval_status !== "approved" && (
                    <button
                      onClick={() => approve(h.id)}
                      data-testid={`approve-hauler-${h.company_name}`}
                      className="px-3 py-1.5 bg-gray-900 text-white text-[10px] uppercase tracking-widest font-semibold hover:bg-emerald-600"
                    >
                      Approve
                    </button>
                  )}
                  <button onClick={() => setEditing(h)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid={`edit-hauler-${h.company_name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(h.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-hauler-${h.company_name}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && <HaulerDialog initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function HaulerDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: initial?.company_name || "",
    ein: initial?.ein || "",
    contact_name: initial?.contact_name || "",
    contact_phone: initial?.contact_phone || "",
    contact_email: initial?.contact_email || "",
    contact_sms_email: initial?.contact_sms_email || "",
    address: initial?.address || "",
    insurance_carrier: initial?.insurance_carrier || "",
    insurance_policy_number: initial?.insurance_policy_number || "",
    insurance_expiry: initial?.insurance_expiry || "",
    w9_on_file: initial?.w9_on_file || false,
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, insurance_expiry: form.insurance_expiry || null };
      if (isEdit) await api.patch(`/haulers/${initial.id}`, payload);
      else await api.post("/haulers", payload);
      toast.success(isEdit ? "Hauler updated" : "Hauler registered");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit Hauler" : "Register Hauler"}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Company Name"><input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="inp" data-testid="hauler-name-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="EIN"><input value={form.ein} onChange={(e) => set("ein", e.target.value)} className="inp font-mono" data-testid="hauler-ein-input" /></F>
            <F label="Address"><input value={form.address} onChange={(e) => set("address", e.target.value)} className="inp" data-testid="hauler-address-input" /></F>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-3">Primary Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Name"><input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="inp" data-testid="hauler-contact-name-input" /></F>
              <F label="Phone"><input required value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="inp" data-testid="hauler-contact-phone-input" /></F>
            </div>
            <div className="mt-3">
              <F label="Email"><input type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="inp" data-testid="hauler-contact-email-input" /></F>
            </div>
            <div className="mt-3">
              <F label="SMS-via-Email Gateway (optional)">
                <input value={form.contact_sms_email} onChange={(e) => set("contact_sms_email", e.target.value)} className="inp font-mono" data-testid="hauler-sms-email-input" placeholder="5551234567@vtext.com" />
              </F>
              <div className="text-[11px] text-gray-500 mt-1">SMS-style texts (approvals, deliveries) are sent to this email-to-SMS gateway address via Resend. Common gateways: <span className="font-mono">@vtext.com</span> (Verizon), <span className="font-mono">@txt.att.net</span> (AT&amp;T), <span className="font-mono">@tmomail.net</span> (T-Mobile).</div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-3">Insurance & Compliance</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Carrier"><input value={form.insurance_carrier} onChange={(e) => set("insurance_carrier", e.target.value)} className="inp" data-testid="hauler-insurance-carrier-input" /></F>
              <F label="Policy #"><input value={form.insurance_policy_number} onChange={(e) => set("insurance_policy_number", e.target.value)} className="inp font-mono" data-testid="hauler-insurance-policy-input" /></F>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Expiry Date"><input type="date" value={form.insurance_expiry} onChange={(e) => set("insurance_expiry", e.target.value)} className="inp font-mono" data-testid="hauler-insurance-expiry-input" /></F>
              <label className="flex items-center gap-2 mt-6 text-sm">
                <input type="checkbox" checked={form.w9_on_file} onChange={(e) => set("w9_on_file", e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="hauler-w9-checkbox" />
                W-9 on file
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-3 border-t border-gray-200">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-hauler-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">{isEdit ? "Update" : "Register"}</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
